import type { ReactNode } from "react";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbScript, defaultOgImageMeta } from "@/lib/seo";

export function LegalPage({
  eyebrow,
  crumb,
  title,
  intro,
  children,
  showPolicyContact = true,
}: {
  eyebrow?: string;
  crumb: { name: string; path: string };
  title?: string;
  intro: ReactNode;
  children?: ReactNode;
  showPolicyContact?: boolean;
}) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader enquireHref="/#enquire" />
      <main>
        <section className="container-page pb-24 pt-20">
          <Breadcrumbs crumbs={[crumb]} />
          {eyebrow && <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-moss">
            {eyebrow}
          </p>}
          {title && <h1 className="max-w-3xl font-serif text-4xl text-charcoal md:text-5xl">
            {title}
          </h1>}
          <div className="mt-8 max-w-[70ch] space-y-5 text-pretty leading-relaxed text-charcoal/70">
            {intro}
          </div>
          {children}
          {showPolicyContact && <div className="mt-12 border-t border-charcoal/10 pt-8 text-sm leading-relaxed text-charcoal/70">
            <p>
              For the current full policy or any questions, contact{" "}
              <a
                href="mailto:info@preserveearth.co.uk"
                className="text-moss underline decoration-moss/30 underline-offset-4 hover:decoration-moss"
              >
                info@preserveearth.co.uk
              </a>
              .
            </p>
            <p className="mt-3 text-xs text-charcoal/70">
              Preserve Earth is a trading name of RBM London Limited · Company
              No. 15482817
            </p>
          </div>}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

export function legalHead({
  title,
  description,
  path,
  crumbName,
  noindex = false,
}: {
  title: string;
  description: string;
  path: string;
  crumbName: string;
  noindex?: boolean;
}) {
  const url = `https://preserveearth.co.uk${path}`;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: url },
      ...defaultOgImageMeta,
      ...(noindex ? [{ name: "robots", content: "noindex, follow" }] : []),
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [breadcrumbScript([{ name: crumbName, path }])],
  };
}
